/* Structure panel — PyCharm-style outline of classes, methods, functions, variables.
 *
 * Parses Python/IronPython code with regex to build a symbol tree,
 * renders it as a clickable list with icons, and supports live updates
 * when the editor content changes.
 */

import { esc } from "./state.js";

// ---------------------------------------------------------------------------
// Python symbol parser
// ---------------------------------------------------------------------------

const _CLASS_RE = /^class\s+(\w+)\s*(?:\(([^)]*)\))?\s*:/;
const _DEF_RE = /^def\s+(\w+)\s*\(([^)]*)\)\s*:/;
const _VAR_RE = /^([A-Z_][A-Z0-9_]{1,})\s*=/;       // UPPER_CASE top-level constants
const _ASSIGN_RE = /^([a-z_]\w*)\s*=\s*[^=]/;         // lowercase assignments
const _SELF_RE = /^self\.(\w+)\s*=/;                   // self.attr = ... inside methods

/**
 * Parse Python source into a symbol tree.
 * Returns [{name, kind, line, params?, children?}]
 */
export function parsePythonSymbols(code) {
  if (!code) return [];
  const lines = code.split("\n");
  const root = [];
  const stack = []; // [{node, indent}]

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const lineNum = i + 1;
    const indent = raw.search(/\S/);
    if (indent < 0) continue; // blank line
    const trimmed = raw.trim();
    if (trimmed.startsWith("#")) continue; // comment

    // Pop stack when we dedent
    while (stack.length && indent <= stack[stack.length - 1].indent) {
      stack.pop();
    }

    // Class
    const cm = trimmed.match(_CLASS_RE);
    if (cm) {
      const node = { name: cm[1], kind: "class", line: lineNum, params: cm[2] || "", children: [] };
      _addToParent(root, stack, node);
      stack.push({ node, indent });
      continue;
    }

    // Function / method
    const dm = trimmed.match(_DEF_RE);
    if (dm) {
      const parentIsClass = stack.length > 0 && stack[stack.length - 1].node.kind === "class";
      const params = dm[2] || "";
      const isPrivate = dm[1].startsWith("_") && !dm[1].startsWith("__");
      const isDunder = dm[1].startsWith("__") && dm[1].endsWith("__");
      const kind = parentIsClass
        ? (isDunder ? "dunder" : isPrivate ? "private_method" : "method")
        : "function";
      const node = { name: dm[1], kind, line: lineNum, params, children: [] };
      _addToParent(root, stack, node);
      stack.push({ node, indent });
      continue;
    }

    // self.attr = ... inside a method → property of the parent class
    if (stack.length > 0) {
      const sm = trimmed.match(_SELF_RE);
      if (sm) {
        // Find enclosing class (walk up stack)
        const attrName = sm[1];
        let classNode = null;
        for (let s = stack.length - 1; s >= 0; s--) {
          if (stack[s].node.kind === "class") { classNode = stack[s].node; break; }
        }
        if (classNode) {
          // Avoid duplicate properties (self.x assigned in multiple methods)
          if (!classNode.children.some(c => c.kind === "property" && c.name === attrName)) {
            classNode.children.push({ name: attrName, kind: "property", line: lineNum });
          }
        }
        continue;
      }
    }

    // Assignments inside functions → local variable children
    if (stack.length > 0) {
      const parentNode = stack[stack.length - 1].node;
      const parentKind = parentNode.kind;
      if (parentKind === "function" || parentKind === "method" || parentKind === "private_method" || parentKind === "dunder") {
        const am = trimmed.match(_ASSIGN_RE);
        if (am) {
          if (!parentNode.children) parentNode.children = [];
          // Only first assignment of each name
          if (!parentNode.children.some(c => c.name === am[1])) {
            parentNode.children.push({ name: am[1], kind: "variable", line: lineNum });
          }
          continue;
        }
      }
    }

    // Top-level constant (UPPER_CASE)
    if (indent === 0) {
      const vm = trimmed.match(_VAR_RE);
      if (vm) {
        root.push({ name: vm[1], kind: "constant", line: lineNum });
        continue;
      }
      // Top-level lowercase assignment (only if not inside a function/class)
      if (stack.length === 0) {
        const am = trimmed.match(_ASSIGN_RE);
        if (am) {
          root.push({ name: am[1], kind: "variable", line: lineNum });
        }
      }
    }
  }

  return root;
}

function _addToParent(root, stack, node) {
  if (stack.length > 0) {
    const parent = stack[stack.length - 1].node;
    if (!parent.children) parent.children = [];
    parent.children.push(node);
  } else {
    root.push(node);
  }
}

// ---------------------------------------------------------------------------
// Icons (inline SVGs matching the PyCharm style)
// ---------------------------------------------------------------------------

// Symbol badges — letter-in-circle/square glyphs not available in Lucide.
// Color comes from .sym-<kind> CSS class so dark theme can override (styles.css).
const ICONS = {
  class:          '<svg class="sym-ico sym-class"   viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">C</text></svg>',
  method:         '<svg class="sym-ico sym-method"  viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">m</text></svg>',
  private_method: '<svg class="sym-ico sym-private" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">m</text></svg>',
  dunder:         '<svg class="sym-ico sym-dunder"  viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">m</text></svg>',
  function:       '<svg class="sym-ico sym-func"    viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">f</text></svg>',
  constant:       '<svg class="sym-ico sym-const"   viewBox="0 0 16 16"><rect x="2" y="2" width="12" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.3"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">C</text></svg>',
  variable:       '<svg class="sym-ico sym-var"     viewBox="0 0 16 16"><rect x="2" y="2" width="12" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.3"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">v</text></svg>',
  property:       '<svg class="sym-ico sym-prop"    viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="9" font-weight="700">p</text></svg>',
};

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

/**
 * Render (or re-render) a structure panel into the given container.
 *
 * @param {HTMLElement} container - The `.structure-body` element to populate
 * @param {string} code - The current file content
 * @param {string} language - File language ("python", "html", etc.)
 * @param {(line: number) => void} onGotoLine - Callback when user clicks a symbol
 */
export function renderStructurePanel(container, code, language, onGotoLine) {
  if (language !== "python") {
    container.innerHTML = '<div class="sp-empty">Structure not available for this file type</div>';
    return;
  }

  const symbols = parsePythonSymbols(code);
  if (!symbols.length) {
    container.innerHTML = '<div class="sp-empty">No classes or functions found</div>';
    return;
  }

  container.innerHTML = "";
  container.appendChild(_buildTree(symbols, onGotoLine, 0, container));
}

function _buildTree(symbols, onGotoLine, depth, rootContainer) {
  const ul = document.createElement("ul");
  ul.className = "sp-tree";
  if (depth > 0) ul.classList.add("sp-nested");

  for (const sym of symbols) {
    const li = document.createElement("li");
    li.className = `sp-item sp-kind-${sym.kind}`;

    const row = document.createElement("div");
    row.className = "sp-row";
    row.innerHTML =
      (ICONS[sym.kind] || ICONS.variable) +
      `<span class="sp-name">${esc(sym.name)}</span>` +
      (sym.params != null ? `<span class="sp-params">(${esc(_truncParams(sym.params))})</span>` : "") +
      `<span class="sp-line">${sym.line}</span>`;

    // Prevent mousedown from stealing focus from Monaco editor
    row.addEventListener("mousedown", (e) => {
      e.preventDefault();
    });

    row.addEventListener("click", (e) => {
      e.stopPropagation();
      // Highlight active
      rootContainer.querySelectorAll(".sp-row.active").forEach(r => r.classList.remove("active"));
      row.classList.add("active");
      onGotoLine(sym.line);
    });

    li.appendChild(row);

    if (sym.children?.length) {
      li.appendChild(_buildTree(sym.children, onGotoLine, depth + 1, rootContainer));
    }

    ul.appendChild(li);
  }

  return ul;
}

function _truncParams(params) {
  // Show abbreviated params: "self, name, value" → "self, name, value"
  // Truncate if very long
  if (params.length > 40) return params.slice(0, 37) + "...";
  return params;
}

// ---------------------------------------------------------------------------
// Create the panel container (called once per file viewer render)
// ---------------------------------------------------------------------------

/**
 * Create the structure panel DOM element with a drag-to-resize handle.
 * Returns {element, body} where body is the scrollable content area.
 */
export function createStructurePanel() {
  const panel = document.createElement("div");
  panel.className = "structure-panel";

  // Resize handle sits on the left edge of the panel
  const resizeHandle = document.createElement("div");
  resizeHandle.className = "sp-resize-handle";

  panel.innerHTML =
    `<div class="sp-header">` +
    `<span class="sp-title">Structure</span>` +
    `<button class="sp-close" title="Close structure panel">&times;</button>` +
    `</div>` +
    `<div class="sp-body"></div>`;

  panel.prepend(resizeHandle);

  const body = panel.querySelector(".sp-body");
  const closeBtn = panel.querySelector(".sp-close");

  closeBtn.addEventListener("click", () => {
    panel.hidden = true;
    panel.dispatchEvent(new CustomEvent('sp-closed'));
  });

  // Drag to resize
  _initResize(resizeHandle, panel);

  return { element: panel, body };
}

const _SP_MIN_W = 140;
const _SP_MAX_W = 500;
const _SP_STORAGE_KEY = "agentcpq_structure_w";

function _initResize(handle, panel) {
  let startX = 0;
  let startW = 0;

  // Restore persisted width
  const saved = localStorage.getItem(_SP_STORAGE_KEY);
  if (saved) {
    const w = parseInt(saved, 10);
    if (w >= _SP_MIN_W && w <= _SP_MAX_W) panel.style.width = w + "px";
  }

  function onMouseDown(e) {
    e.preventDefault();
    startX = e.clientX;
    startW = panel.offsetWidth;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }

  function onMouseMove(e) {
    // Panel is on the right, so dragging left = wider
    const delta = startX - e.clientX;
    const newW = Math.max(_SP_MIN_W, Math.min(_SP_MAX_W, startW + delta));
    panel.style.width = newW + "px";
  }

  function onMouseUp() {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    localStorage.setItem(_SP_STORAGE_KEY, String(panel.offsetWidth));
  }

  handle.addEventListener("mousedown", onMouseDown);
}
